import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const RADIUS = 3.2;

function buildNodes(skills, projects) {
  const norm = s => s.toLowerCase().replace(/\.js$/, '').replace(/[\s.-]/g, '');
  const nodes = [];
  const map = new Map();

  skills.forEach(cat => {
    cat.tags.forEach(tag => {
      const matching = projects.filter(p =>
        p.stack.some(s => {
          const n = norm(s);
          const t = norm(tag);
          return n === t || (t.length > 3 && n.includes(t)) || (n.length > 3 && t.includes(n));
        })
      );
      if (matching.length === 0 && !['Express', 'Flask', 'SQLAlchemy', 'SQL Server', 'pytest', 'Bug Triage', 'Regression Testing', 'E2E Testing', 'Windsurf', 'Replit', 'Zod', 'Replit'].includes(tag)) return;

      const id = `${cat.name}:${tag}`;
      const node = {
        id,
        text: tag,
        cat: cat.name,
        color: cat.color,
        projects: matching,
      };
      nodes.push(node);
      map.set(tag, node);
    });
  });

  // Connections: two techs are linked if they appear in the same project
  const connections = [];
  const connSet = new Set();
  nodes.forEach((nA, iA) => {
    nA.projects.forEach(p => {
      p.stack.forEach(stackTag => {
        const n = norm(stackTag);
        nodes.forEach((nB, iB) => {
          if (iA >= iB) return;
          const t = norm(nB.text);
          if (n === t || (t.length > 3 && n.includes(t)) || (n.length > 3 && t.includes(n))) {
            const key = `${iA}-${iB}`;
            if (!connSet.has(key)) {
              connSet.add(key);
              connections.push({ a: iA, b: iB, project: p.title });
            }
          }
        });
      });
    });
  });

  return { nodes, connections };
}

function distributeOnSphere(count) {
  const pts = [];
  const phi = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = phi * i;
    pts.push(new THREE.Vector3(Math.cos(theta) * r * RADIUS, y * RADIUS, Math.sin(theta) * r * RADIUS));
  }
  return pts;
}

function makeStarTexture() {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.2, 'rgba(255,255,255,0.8)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.2)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

function TechConstellation({ skills, projects, onHover }) {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { onHover };
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const { nodes, connections } = buildNodes(skills, projects);
    const positions = distributeOnSphere(nodes.length);

    const w = mount.clientWidth;
    const h = mount.clientHeight || 500;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
    camera.position.set(0, 0, 9);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);

    const starTex = makeStarTexture();

    // Stars
    const starMaterials = nodes.map(n => new THREE.SpriteMaterial({
      map: starTex,
      color: new THREE.Color(n.color),
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }));

    const stars = nodes.map((node, i) => {
      const sprite = new THREE.Sprite(starMaterials[i]);
      sprite.position.copy(positions[i]);
      const baseScale = node.projects.length > 0 ? 0.35 + node.projects.length * 0.06 : 0.22;
      sprite.scale.setScalar(baseScale);
      sprite.userData = { node, baseScale, index: i };
      group.add(sprite);
      return sprite;
    });

    // Connection lines
    const linePositions = [];
    const lineColors = [];
    connections.forEach(c => {
      const pA = positions[c.a];
      const pB = positions[c.b];
      linePositions.push(pA.x, pA.y, pA.z, pB.x, pB.y, pB.z);
      const col = new THREE.Color(nodes[c.a].color);
      lineColors.push(col.r, col.g, col.b, col.r, col.g, col.b);
    });

    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
    lineGeo.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 3));
    const lineMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.12,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const lines = new THREE.LineSegments(lineGeo, lineMat);
    group.add(lines);

    // Raycaster
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2(-2, -2);
    let hovered = null;
    let dragging = false;
    let dragStart = { x: 0, y: 0 };
    let velY = 0.002;
    let velX = 0;
    let rotY = 0;
    let rotX = 0;
    let hasPointer = false;

    function onDown(e) {
      dragging = true;
      dragStart = { x: e.clientX, y: e.clientY };
    }

    function onMove(e) {
      const rect = mount.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      hasPointer = true;

      if (dragging) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        velY = dx * 0.0008;
        velX = dy * 0.0008;
        dragStart = { x: e.clientX, y: e.clientY };
      }
    }

    function onUp() {
      if (!dragging) return;
      dragging = false;
      mount.style.cursor = hovered ? 'pointer' : 'grab';
    }

    function onLeave() {
      hasPointer = false;
      pointer.set(-2, -2);
      if (hovered) {
        hovered = null;
        mount.style.cursor = 'grab';
        stateRef.current.onHover?.(null);
        setTooltip(null);
      }
    }

    mount.addEventListener('pointermove', onMove);
    mount.addEventListener('pointerdown', onDown);
    mount.addEventListener('pointerup', onUp);
    mount.addEventListener('pointerleave', onLeave);
    mount.style.cursor = 'grab';

    // Resize
    const ro = new ResizeObserver(() => {
      const nw = mount.clientWidth;
      const nh = mount.clientHeight || 500;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    });
    ro.observe(mount);

    // Visibility
    let visible = true;
    const io = new IntersectionObserver(es => {
      visible = es[0]?.isIntersecting ?? false;
    });
    io.observe(mount);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf;

    function tick() {
      raf = requestAnimationFrame(tick);
      if (!visible) return;

      if (!dragging) {
        const targetVel = hovered ? 0.0003 : velY * 0.99;
        velY = hovered ? velY * 0.92 : targetVel;
        velX *= 0.92;
      }
      if (!reduced || dragging) {
        rotY += velY;
        rotX += velX;
        rotX = THREE.MathUtils.clamp(rotX, -1.0, 1.0);
        group.rotation.y = rotY;
        group.rotation.x = rotX;
      }

      // Hover detection
      let next = null;
      if (hasPointer && !dragging) {
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects(stars, false);
        next = hits.length ? hits[0].object : null;
      }
      if (next !== hovered) {
        hovered = next;
        mount.style.cursor = next ? 'pointer' : 'grab';
        if (next) {
          stateRef.current.onHover?.(next.userData.node);
          const rect = mount.getBoundingClientRect();
          const screenPos = next.position.clone();
          screenPos.project(camera);
          const sx = (screenPos.x + 1) / 2 * rect.width;
          const sy = -(screenPos.y - 1) / 2 * rect.height;
          setTooltip({ x: sx, y: sy, node: next.userData.node });
        } else {
          stateRef.current.onHover?.(null);
          setTooltip(null);
        }
      } else if (hovered) {
        // Update tooltip position as star rotates
        const rect = mount.getBoundingClientRect();
        const screenPos = hovered.position.clone();
        screenPos.applyMatrix4(group.matrixWorld);
        screenPos.project(camera);
        const sx = (screenPos.x + 1) / 2 * rect.width;
        const sy = -(screenPos.y - 1) / 2 * rect.height;
        setTooltip(prev => prev ? { ...prev, x: sx, y: sy } : prev);
      }

      stars.forEach(s => {
        const isHover = s === hovered;
        const targetScale = isHover ? s.userData.baseScale * 1.8 : s.userData.baseScale;
        const targetOpacity = isHover ? 1 : 0.7;

        s.scale.x += (targetScale - s.scale.x) * 0.15;
        s.scale.y += (targetScale - s.scale.y) * 0.15;
        s.material.opacity += (targetOpacity - s.material.opacity) * 0.15;
      });

      renderer.render(scene, camera);
    }

    tick();

    return () => {
      cancelAnimationFrame(raf);
      mount.removeEventListener('pointermove', onMove);
      mount.removeEventListener('pointerdown', onDown);
      mount.removeEventListener('pointerup', onUp);
      mount.removeEventListener('pointerleave', onLeave);
      ro.disconnect();
      io.disconnect();
      renderer.dispose();
      starTex.dispose();
      starMaterials.forEach(m => m.dispose());
      lineGeo.dispose();
      lineMat.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, [skills, projects]);

  return (
    <div className="constellation-wrap">
      <div ref={mountRef} className="constellation" />
      {tooltip && (
        <div
          ref={tooltipRef}
          className="constellation-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <span className="constellation-dot" style={{ background: tooltip.node.color }} />
          <span className="constellation-name">{tooltip.node.text}</span>
          <span className="constellation-cat">{tooltip.node.cat}</span>
        </div>
      )}
    </div>
  );
}

export default TechConstellation;
